import { useEffect, useRef } from 'react';

const STICK_THRESHOLD_PX = 64;

/**
 * Sticky-to-bottom scroll behavior for chat-style feeds. Returns a ref to
 * attach to the scrollable container.
 *
 * - On every dep change, scrolls the container to the bottom *only if* the
 *   user was already at (or within {@link STICK_THRESHOLD_PX} of) the bottom.
 * - If the user has scrolled up to read earlier content, new deltas do not
 *   snap them back. Scrolling back down re-enables stickiness.
 */
export function useStickToBottom<T extends HTMLElement>(
  deps: ReadonlyArray<unknown>,
) {
  const ref = useRef<T | null>(null);
  const stuckRef = useRef(true);

  useEffect(() => {
    const el = ref.current;
    // Defensive: the consumer always passes the ref to a mounted element
    // before the effect runs, but guard the null case so a misuse (e.g. ref
    // attached to a conditionally-mounted node) doesn't throw.
    /* v8 ignore next */
    if (!el) return;
    const onScroll = () => {
      const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
      stuckRef.current = dist <= STICK_THRESHOLD_PX;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!stuckRef.current) return;
    const el = ref.current;
    /* v8 ignore next */
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    // deps is intentionally the consumer's signal list; eslint can't verify
    // its shape so disable the deps-rule for this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return ref;
}
